import { useFetcher } from 'react-router';
import { useMemoizedFn } from 'ahooks';
import { useEffect, useRef } from 'react';
import { removeLocalFavouritedItems } from '~/modules/moodboards/util/design-board-utils';
import { useSetAtom } from 'jotai';
import {
    enquiryAutoSubmitAtom,
    enquirySessionAtom,
} from '~/modules/enquiry/hooks/use-enquiry';
import { clearEnquiryDraft } from '~/modules/enquiry/util/clear-enquiry-draft';
import type { UserLogoutAction } from '~/routes/remix-api.user.logout';

export const useLogout = (redirectUrl?: string) => {
    const logoutFetcher = useFetcher<UserLogoutAction>();
    const setEnquirySession = useSetAtom(enquirySessionAtom);
    const setEnquiryAutoSubmit = useSetAtom(enquiryAutoSubmitAtom);
    const logoutSubmitted = useRef(false);

    const onLogoutClick = useMemoizedFn(() => {
        // Empty the atoms, not just the keys behind them. `atomWithStorage`
        // keeps the draft in memory, so removing the key alone leaves a mounted
        // form holding it — and this reload is not instant. Logging out flips
        // `isLoggedIn`, which drops the BuildHub block, whose cleanup effect
        // writes the whole form back. That put the contact details on disk
        // again between the clear and the reload.
        setEnquirySession(null);
        setEnquiryAutoSubmit(false);
        // Still the keys as well: nothing guarantees a form is mounted to hold
        // the atom, and a draft left on disk is one the logged-out form fills
        // itself in from when the page comes back.
        clearEnquiryDraft();
        logoutFetcher.submit(redirectUrl ? { redirectUrl } : {}, {
            method: 'post',
            action: `/remix-api/user/logout`,
        });
        logoutSubmitted.current = true;
    });

    const logoutSideEffects = useMemoizedFn(() => {
        removeLocalFavouritedItems();
        setEnquirySession(null);
        setEnquiryAutoSubmit(false);
        clearEnquiryDraft();
        window.location.reload();
    });

    useEffect(() => {
        if (!logoutSubmitted.current || logoutFetcher.state !== 'idle') return;
        logoutSubmitted.current = false;
        window.location.reload();
    }, [logoutFetcher.state]);

    return { onLogoutClick, logoutFetcher, logoutSideEffects };
};
